import { useState } from 'react'
import VoiceTextarea from '@/components/VoiceTextarea'
import {
  Table, Button, Tag, Space, Popconfirm, Modal, Form,
  Input, Select, DatePicker, message, Empty, Tooltip,
} from 'antd'
import {
  PlusOutlined, SendOutlined, PauseOutlined,
  PlayCircleOutlined, DeleteOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  useMessaggiProgrammati,
  useCreateMessaggioProgrammato,
  useUpdateMessaggioProgrammato,
  useDeleteMessaggioProgrammato,
  useInviaNowMessaggioProgrammato,
  type MessaggioProgrammato,
} from '@/hooks/useMessaggiProgrammati'

const RICORRENZA_LABELS: Record<string, string> = {
  nessuna: 'Una volta',
  settimanale: 'Ogni settimana',
  mensile: 'Ogni mese',
  annuale: 'Ogni anno',
}

const STATO_COLORS: Record<string, string> = {
  attivo: 'green',
  sospeso: 'orange',
  completato: 'default',
}

export function MessaggiProgrammati() {
  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form] = Form.useForm()

  const { data: messaggi = [], isLoading } = useMessaggiProgrammati()
  const createMut = useCreateMessaggioProgrammato()
  const updateMut = useUpdateMessaggioProgrammato()
  const deleteMut = useDeleteMessaggioProgrammato()
  const inviaNowMut = useInviaNowMessaggioProgrammato()

  const handleOpenCreate = () => {
    setEditingId(null)
    form.resetFields()
    setFormOpen(true)
  }

  const handleOpenEdit = (record: MessaggioProgrammato) => {
    setEditingId(record.id)
    form.setFieldsValue({
      ...record,
      data_primo_invio: record.data_primo_invio ? dayjs(record.data_primo_invio) : null,
    })
    setFormOpen(true)
  }

  const handleClose = () => {
    setFormOpen(false)
    form.resetFields()
    setEditingId(null)
  }

  const handleSave = async (values: any) => {
    const payload = {
      ...values,
      data_primo_invio: values.data_primo_invio?.toISOString(),
      tipo: values.tipo || 'auto',
      ricorrenza: values.ricorrenza || 'nessuna',
    }
    try {
      if (editingId) {
        await updateMut.mutateAsync({ id: editingId, ...payload })
        message.success('Messaggio programmato aggiornato')
      } else {
        await createMut.mutateAsync(payload)
        message.success('Messaggio programmato creato')
      }
      handleClose()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err.response?.data?.detail || 'Errore salvataggio')
    }
  }

  const handleToggleStato = (record: MessaggioProgrammato) => {
    updateMut.mutate({
      id: record.id,
      stato: record.stato === 'attivo' ? 'sospeso' : 'attivo',
    } as any)
  }

  const handleInviaNow = async (id: number) => {
    try {
      await inviaNowMut.mutateAsync(id)
      message.success('Messaggio inviato')
    } catch {
      message.error('Errore invio')
    }
  }

  const columns = [
    {
      title: 'Oggetto / Destinatario',
      key: 'oggetto',
      render: (_: any, r: MessaggioProgrammato) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {r.oggetto || `${r.destinatario_nome || ''} ${r.destinatario_cognome || ''}`.trim() || r.destinatario_numero}
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{r.destinatario_numero}</div>
        </div>
      ),
    },
    {
      title: 'Canale',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 100,
      render: (v: string) => <Tag color="blue">{v === 'auto' ? 'Auto' : v.toUpperCase()}</Tag>,
    },
    {
      title: 'Ricorrenza',
      dataIndex: 'ricorrenza',
      key: 'ricorrenza',
      width: 130,
      render: (v: string) => RICORRENZA_LABELS[v] ?? v,
    },
    {
      title: 'Prossimo invio',
      dataIndex: 'prossimo_invio',
      key: 'prossimo',
      width: 150,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Invii',
      dataIndex: 'invii_totali',
      key: 'invii',
      width: 60,
    },
    {
      title: 'Stato',
      dataIndex: 'stato',
      key: 'stato',
      width: 100,
      render: (v: string) => <Tag color={STATO_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: '',
      key: 'azioni',
      width: 130,
      render: (_: any, r: MessaggioProgrammato) => (
        <Space>
          <Tooltip title="Invia ora">
            <Button
              size="small"
              icon={<SendOutlined />}
              loading={inviaNowMut.isPending}
              onClick={() => handleInviaNow(r.id)}
              disabled={r.stato === 'completato'}
            />
          </Tooltip>
          <Tooltip title={r.stato === 'attivo' ? 'Sospendi' : 'Riattiva'}>
            <Button
              size="small"
              icon={r.stato === 'attivo' ? <PauseOutlined /> : <PlayCircleOutlined />}
              onClick={() => handleToggleStato(r)}
              disabled={r.stato === 'completato'}
            />
          </Tooltip>
          <Popconfirm
            title="Eliminare questo messaggio programmato?"
            onConfirm={() => deleteMut.mutate(r.id)}
            okText="Sì"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Nuovo programmato
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={messaggi}
        rowKey="id"
        loading={isLoading}
        size="small"
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: <Empty description="Nessun messaggio programmato" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        onRow={(record) => ({ onDoubleClick: () => handleOpenEdit(record) })}
      />

      <Modal
        title={editingId ? 'Modifica messaggio programmato' : 'Nuovo messaggio programmato'}
        open={formOpen}
        onCancel={handleClose}
        footer={null}
        width="min(600px, 95vw)"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave} style={{ marginTop: 8 }}>
          <Form.Item name="oggetto" label="Oggetto / Descrizione interna">
            <Input placeholder="Es: Promemoria cambio gomme autunnale" />
          </Form.Item>

          <Form.Item
            name="destinatario_numero"
            label="Numero destinatario"
            rules={[{ required: true, message: 'Inserisci il numero' }]}
          >
            <Input placeholder="+39..." />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item name="destinatario_nome" label="Nome">
              <Input placeholder="Mario" />
            </Form.Item>
            <Form.Item name="destinatario_cognome" label="Cognome">
              <Input placeholder="Rossi" />
            </Form.Item>
          </div>

          <Form.Item name="tipo" label="Canale" initialValue="auto">
            <Select>
              <Select.Option value="auto">Automatico (WhatsApp → SMS)</Select.Option>
              <Select.Option value="whatsapp">WhatsApp</Select.Option>
              <Select.Option value="sms">SMS</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="testo"
            label="Testo messaggio"
            rules={[{ required: true, message: 'Inserisci il testo' }]}
          >
            <VoiceTextarea rows={4} minHeight={100} maxHeight={250} placeholder="Testo del messaggio..." label="Testo messaggio" compact />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name="data_primo_invio"
              label="Data/ora primo invio"
              rules={[{ required: true, message: 'Seleziona data e ora' }]}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="DD/MM/YYYY HH:mm"
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item name="ricorrenza" label="Ricorrenza" initialValue="nessuna">
              <Select>
                <Select.Option value="nessuna">Una volta sola</Select.Option>
                <Select.Option value="settimanale">Ogni settimana</Select.Option>
                <Select.Option value="mensile">Ogni mese</Select.Option>
                <Select.Option value="annuale">Ogni anno</Select.Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={handleClose}>Annulla</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMut.isPending || updateMut.isPending}
              >
                {editingId ? 'Salva modifiche' : 'Crea'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
